import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { formatINR } from "@/lib/i18n";
import { Printer, ArrowLeft, ShieldCheck } from "lucide-react";

export default function TaxReceipt() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get(`/tax/receipts/${id}`).then((res) => setR(res.data));
  }, [id]);

  if (!r) return <div className="p-10 text-slate-500">Loading…</div>;

  const heading = lang === "hi" ? r.panchayat?.name_hi || r.panchayat?.name : r.panchayat?.name;

  return (
    <div className="min-h-screen py-8 px-4 bg-[#FDFBF7]">
      <div className="max-w-3xl mx-auto">
        {/* Action bar (hidden on print) */}
        <div className="no-print flex items-center justify-between mb-6">
          <Link to="/tax" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0A3161]"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <div className="flex items-center gap-2">
            {r.approval_status === "pending" && (
              <span className="px-2.5 py-1 text-[10px] uppercase tracking-wider rounded font-semibold bg-amber-50 text-amber-800 border border-amber-200" data-testid="receipt-pending-badge">
                {t("pendingApproval")}
              </span>
            )}
            {r.approval_status === "rejected" && (
              <span className="px-2.5 py-1 text-[10px] uppercase tracking-wider rounded font-semibold bg-red-50 text-red-700 border border-red-200">
                Rejected: {r.rejected_reason || "—"}
              </span>
            )}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tax/receipts/${r.id}/pdf`}
               target="_blank" rel="noreferrer"
               data-testid="receipt-pdf-btn"
               className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-[#0A3161] text-[#0A3161]">
              <Printer className="w-4 h-4" /> {t("downloadPdf")}
            </a>
            <button onClick={() => window.print()} data-testid="print-receipt-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
              <Printer className="w-4 h-4" /> {t("print")}
            </button>
          </div>
        </div>

        {/* Receipt */}
        <div className="print-page bg-white border border-slate-300 rounded-md p-10 relative gov-watermark shadow-sm" data-testid="receipt-card">
          <div className="ashoka-stripe absolute top-0 left-0 right-0" />

          <div className="relative z-10">
            {/* Header */}
            <div className="text-center pb-5 border-b-2 border-[#0A3161]">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#E87722] font-semibold">{t("govOfHaryana")}</div>
              <div className="font-heading text-lg font-bold mt-1 text-[#0A3161]">{t("panchayatiRaj")}</div>
              <div className="mt-3 font-heading text-2xl font-bold text-slate-900">Gram Panchayat — {heading}</div>
              <div className="text-xs text-slate-500 mt-0.5">{r.panchayat?.block}, {r.panchayat?.district}, Haryana</div>
              <div className="mt-5 inline-flex items-center gap-2 px-4 py-1.5 bg-[#0A3161] text-white text-xs uppercase tracking-[0.2em] rounded">
                Tax Payment Receipt
              </div>
            </div>

            {/* Meta */}
            <div className="grid grid-cols-2 gap-6 mt-6 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("receiptNo")}</div>
                <div className="font-mono font-bold text-base text-slate-900">{r.receipt_no}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("paidAt")}</div>
                <div className="font-mono text-base text-slate-900">{new Date(r.paid_at).toLocaleString("en-IN")}</div>
              </div>
            </div>

            {/* Payer */}
            <div className="mt-6 rounded-md border border-slate-200 p-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("owner")}</div>
                <div className="font-semibold text-slate-900">{r.property?.owner_name}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("propertyNo")}</div>
                <div className="font-mono text-slate-900">{r.property?.property_no}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("address")}</div>
                <div className="text-slate-900">{r.property?.address}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("ward")}</div>
                <div className="text-slate-900">{r.property?.ward}</div>
              </div>
            </div>

            {/* Demand details */}
            <table className="w-full mt-6 text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
                <tr>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-right px-3 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["houseTax", "house_tax"],
                  ["waterTax", "water_tax"],
                  ["streetLightTax", "street_light_tax"],
                  ["conservancyTax", "conservancy_tax"],
                  ["drainageTax", "drainage_tax"],
                ].map(([key, field]) => (
                  <tr key={field} className="border-b border-slate-100">
                    <td className="px-3 py-2">{t(key)} ({r.demand?.financial_year})</td>
                    <td className="px-3 py-2 text-right font-mono">{formatINR(r.demand?.[field] || 0)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 border-t-2 border-slate-300">
                  <td className="px-3 py-3 font-bold text-slate-900">Total Demand</td>
                  <td className="px-3 py-3 text-right font-mono font-bold">{formatINR(r.demand?.total_amount)}</td>
                </tr>
                <tr>
                  <td className="px-3 py-3 font-bold text-[#107C10]">Amount Paid (this receipt)</td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-[#107C10] text-lg">{formatINR(r.amount_paid)}</td>
                </tr>
              </tbody>
            </table>

            {/* Payment mode */}
            <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t("paymentMode")}</div>
                <div className="font-semibold uppercase">{r.payment_mode}</div>
              </div>
              {r.payment_ref && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Reference</div>
                  <div className="font-mono">{r.payment_ref}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Payer</div>
                <div className="font-semibold">{r.payer_name || "—"}</div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-10 pt-5 border-t border-slate-200 flex items-end justify-between text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#107C10]" />
                <span>{t("digitalSignature")}</span>
              </div>
              <div className="text-right">
                <div className="border-t border-slate-300 w-40 pt-1 mt-6">{t("secretary")}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
